import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import * as Icons from "lucide-react";
import { Zap, Lock, Check, Banknote, Gem, Trophy, ArrowLeft, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { api, errMsg, naira } from "@/lib/api";

const pascal = (s) => s.split("-").map((x) => x[0].toUpperCase() + x.slice(1)).join("");
const RED_STATUS = {
  fulfilled: { label: "done", c: "text-[#16A34A]" },
  pending_review: { label: "under review", c: "text-[#FFB300]" },
  approved: { label: "approved", c: "text-[#00E5FF]" },
  paid: { label: "paid", c: "text-[#16A34A]" },
  rejected: { label: "rejected", c: "text-[#FF0055]" },
};

export default function HustlerRewards() {
  const nav = useNavigate();
  const { setCharacter } = useAuth();
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(null);

  const load = useCallback(async () => {
    try { setData((await api.get("/hustler/state")).data); }
    catch (e) { toast.error(errMsg(e)); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const redeem = async (r) => {
    setBusy(r.id);
    try {
      const { data: res } = await api.post("/hustler/redeem", { reward_id: r.id });
      if (res.character) setCharacter(res.character);
      toast.success(res.message || "Redeemed!");
      await load();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(null); }
  };

  if (!data) return <div className="min-h-[60vh] flex items-center justify-center text-slate-400">loading…</div>;
  const unlocked = data.achievements.filter((a) => a.unlocked).length;

  return (
    <div className="px-5 pt-6 pb-6" data-testid="hustler-page">
      <button onClick={() => nav("/room")} className="flex items-center gap-1 label-xs text-slate-500 mb-2"><ArrowLeft size={14} /> Home</button>
      <span className="label-xs text-slate-500">Hustler Rewards</span>
      <h1 className="font-display text-3xl mt-1">Play. Build. Compete. <span className="text-[#FFD700]">Get Rewarded.</span></h1>

      {/* points banner */}
      <div className="mt-4 rounded-2xl bg-gradient-to-br from-[#FFD700]/20 via-[#FF9100]/10 to-transparent border border-[#FFD700]/30 p-4 flex items-center gap-4" data-testid="hp-balance">
        <div className="w-14 h-14 rounded-2xl bg-[#FFD700]/20 flex items-center justify-center"><Zap size={28} className="text-[#FFD700]" /></div>
        <div className="flex-1">
          <div className="font-display text-3xl text-[#FFD700]">{data.points}</div>
          <div className="label-xs text-slate-500">Hustler Points · {data.lifetime_points} earned all-time</div>
        </div>
        <div className="text-right label-xs text-slate-500">{unlocked}/{data.achievements.length}<br/>achievements</div>
      </div>
      <p className="mt-2 label-xs text-slate-500">Points are earned only through real accomplishments — never idle time. No bots, no farming.</p>

      {/* Rewards catalog */}
      <h2 className="font-display text-xl mt-6 mb-2">Redeem</h2>
      <div className="space-y-2">
        {data.rewards.map((r) => {
          const locked = r.kind === "cash" && !data.payouts_enabled;
          const afford = data.points >= r.cost_points;
          const Icon = r.kind === "cash" ? Banknote : r.kind === "gems" ? Gem : Icons.Wallet;
          return (
            <div key={r.id} data-testid={`reward-${r.id}`}
              className={`rounded-xl border p-3 flex items-center gap-3 ${locked ? "border-slate-200 bg-[#101010] opacity-80" : "border-slate-200 bg-white"}`}>
              <div className="w-10 h-10 rounded-lg bg-black/50 flex items-center justify-center shrink-0">
                <Icon size={18} className={r.kind === "cash" ? "text-[#16A34A]" : "text-[#FFD700]"} />
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-sm font-semibold flex items-center gap-1.5">{r.title}{locked && <Lock size={12} className="text-slate-500" />}</div>
                <div className="label-xs text-slate-500">{r.desc}</div>
              </div>
              {locked ? (
                <span className="label-xs text-[#FFB300] text-right">Coming<br/>soon 🔒</span>
              ) : (
                <button data-testid={`redeem-${r.id}`} disabled={!afford || busy === r.id} onClick={() => redeem(r)}
                  className={`label-xs px-3 py-2 rounded-lg font-bold shrink-0 ${afford ? "bg-[#FFD700] text-slate-900" : "bg-black/40 text-slate-400"}`}>
                  {r.cost_points} HP
                </button>
              )}
            </div>
          );
        })}
      </div>
      {!data.payouts_enabled && (
        <div className="mt-2 flex items-start gap-1.5 label-xs text-slate-500 bg-white border border-slate-200 rounded-lg px-2.5 py-2">
          <ShieldCheck size={13} className="text-[#16A34A] mt-0.5 shrink-0" /> Real cash rewards unlock after legal verification. Game Naira & gems have no real-world value and can't be withdrawn.
        </div>
      )}

      {/* Achievements */}
      <h2 className="font-display text-xl mt-6 mb-2">Achievements</h2>
      <div className="grid grid-cols-2 gap-2">
        {data.achievements.map((a) => {
          const Icon = Icons[pascal(a.icon)] || Trophy;
          return (
            <div key={a.id} data-testid={`ach-${a.id}`}
              className={`rounded-xl border p-3 ${a.unlocked ? "border-[#16A34A]/40 bg-[#16A34A]/5" : "border-white/8 bg-[#101010]"}`}>
              <div className="flex items-center justify-between">
                <Icon size={18} className={a.unlocked ? "text-[#16A34A]" : "text-slate-400"} />
                {a.unlocked ? <Check size={14} className="text-[#16A34A]" /> : <Lock size={12} className="text-slate-400" />}
              </div>
              <div className={`mt-2 text-sm font-semibold ${a.unlocked ? "" : "text-slate-500"}`}>{a.title}</div>
              <div className="label-xs text-slate-500 mt-0.5 leading-tight">{a.desc}</div>
              <div className="label-xs text-[#FFD700] mt-1">+{a.points} HP</div>
            </div>
          );
        })}
      </div>

      {/* Redemption history */}
      {data.redemptions.length > 0 && (
        <>
          <h2 className="font-display text-xl mt-6 mb-2">History</h2>
          <div className="space-y-1.5">
            {data.redemptions.map((r) => {
              const st = RED_STATUS[r.status] || { label: r.status, c: "text-slate-500" };
              return (
                <div key={r.id} className="flex items-center justify-between bg-white border border-white/8 rounded-lg px-3 py-2 label-xs">
                  <span className="text-slate-600">{r.title}</span>
                  <span className={st.c}>−{r.points_spent} HP · {st.label}</span>
                </div>
              );
            })}
          </div>
        </>
      )}

      <button onClick={() => nav("/contests")} data-testid="go-contests"
        className="mt-6 w-full py-3 rounded-xl bg-[#FF0055]/15 text-[#FF0055] font-bold flex items-center justify-center gap-2">
        <Trophy size={16} /> See skill contests & win prizes
      </button>
    </div>
  );
}
